import { useEffect, useState, useContext, useRef } from "react";
import { createBrowserRouter, RouterProvider, useNavigate, useParams } from "react-router";
import axios from 'axios';

import { AppContext } from '../../../context.js';

import Helmet from "../../components/helmet.js";
import { Copy, FilePlus, UserPlus } from "../../../files/icons/index.js";
import { Button } from "../../components/component.js";

function Partner() {
  const context = useContext(AppContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  // General information
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Partner Portal
  const [partnerPortal, setPartnerPortal] = useState(false);
  const [partnerPortalLogin, setPartnerPortalLogin] = useState("");
  // Only set right after a password is generated; the server stores just the hash
  const [partnerPortalPassword, setPartnerPortalPassword] = useState("");

  // Referral Provider
  const [referralProvider, setReferralProvider] = useState(false);

  // Service Provider
  const [serviceProvider, setServiceProvider] = useState(false);

  // Services
  const [services, setServices] = useState([]);

  // Service Modal
  const [serviceModal, setServiceModal] = useState({
    visible: false,
    mode: 'create', // 'create' or 'edit'
    serviceID: null,
    title: '',
    description: '',
    code: '',
    price: 0
  });

  useEffect(() => {
    let u = context.addSpiner();
    axios.post('/api/admin/partners', {
      filter: {
        id: [id]
      }
    }).then((response) => {
      setTitle(response.data.partners[0].title || "");
      setDescription(response.data.partners[0].description || "");
      
      setPartnerPortal(response.data.partners[0].partnerPortal || false);
      setPartnerPortalLogin(response.data.partners[0].partnerPortalLogin || "");
      setPartnerPortalPassword("");

      setReferralProvider(response.data.partners[0].referralProvider || false);

      setServiceProvider(response.data.partners[0].serviceProvider || false);
      setServices(response.data.partners[0].services || []);

      setLoading(false);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, [id]);

  // General information
  function updateTitle() {
    let u = context.addSpiner();
    axios.post('/api/admin/partners/updateTitle', {
      partnerID: id,
      title: title,
    }).then((response) => {
      
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateDescription() {
    let u = context.addSpiner();
    axios.post('/api/admin/partners/updateDescription', {
      partnerID: id,
      description: description,
    }).then((response) => {
      
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Partner Portal
  function updatePartnerPortal(value) {
    if (partnerPortal === value) {
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/partners/updatePartnerPortal', {
      partnerID: id,
      partnerPortal: value,
    }).then((response) => {
      setPartnerPortal(value);
      setPartnerPortalLogin(response.data.partnerPortalLogin);
      setPartnerPortalPassword(response.data.partnerPortalPassword || "");
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function resetPartnerPortalPassword() {
    let u = context.addSpiner();
    axios.post('/api/admin/partners/resetPartnerPortalPassword', {
      partnerID: id,
    }).then((response) => {
      setPartnerPortalPassword(response.data.partnerPortalPassword);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function copyPartnerPortalLogin() {
    navigator.clipboard.writeText(partnerPortalLogin);
  }

  function copyPartnerPortalPassword() {
    navigator.clipboard.writeText(partnerPortalPassword);
  }

  // Referral Provider
  function updateReferralProvider(value) {
    if (referralProvider === value) {
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/partners/updateReferralProvider', {
      partnerID: id,
      referralProvider: value,
    }).then((response) => {
      setReferralProvider(value);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Service Provider
  function updateServiceProvider(value) {
    if (serviceProvider === value) {
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/partners/updateServiceProvider', {
      partnerID: id,
      serviceProvider: value,
    }).then((response) => {
      setServiceProvider(value);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Service Management
  function openCreateServiceModal() {
    setServiceModal({
      visible: true,
      mode: 'create',
      serviceID: null,
      title: '',
      description: '',
      code: '',
      price: 0
    });
  }

  function openEditServiceModal(service) {
    setServiceModal({
      visible: true,
      mode: 'edit',
      serviceID: service.id,
      title: service.title,
      description: service.description || '',
      code: service.code,
      price: service.price
    });
  }

  function closeServiceModal() {
    setServiceModal({
      visible: false,
      mode: 'create',
      serviceID: null,
      title: '',
      description: '',
      code: '',
      price: 0
    });
  }

  function createService() {
    let u = context.addSpiner();
    axios.post('/api/admin/partners/services/create', {
      partnerID: id,
      title: serviceModal.title,
      description: serviceModal.description || null,
      code: serviceModal.code,
      price: serviceModal.price
    }).then((response) => {
      // Refresh services list
      axios.post('/api/admin/partners', {
        filter: {
          id: [id]
        }
      }).then((response) => {
        setServices(response.data.partners[0].services || []);
        closeServiceModal();
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateService() {
    let u = context.addSpiner();
    axios.post('/api/admin/partners/services/update', {
      partnerID: id,
      serviceID: serviceModal.serviceID,
      title: serviceModal.title,
      description: serviceModal.description || null,
      code: serviceModal.code,
      price: serviceModal.price
    }).then((response) => {
      // Refresh services list
      axios.post('/api/admin/partners', {
        filter: {
          id: [id]
        }
      }).then((response) => {
        setServices(response.data.partners[0].services || []);
        closeServiceModal();
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function deleteService(serviceID) {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/partners/services/delete', {
      partnerID: id,
      serviceID: serviceID
    }).then((response) => {
      // Refresh services list
      axios.post('/api/admin/partners', {
        filter: {
          id: [id]
        }
      }).then((response) => {
        setServices(response.data.partners[0].services || []);
      });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function handleServiceModalSubmit() {
    if (serviceModal.mode === 'create') {
      createService();
    } else {
      updateService();
    }
  }

  function renderServices() {
    // Filter out archived services (status = false)
    const activeServices = services.filter(service => service.status !== false);

    if (!activeServices || activeServices.length === 0) {
      return (
        <div className="card-header sm">
          <div className="typo-normal-150 text-center">No services available</div>
        </div>
      );
    }

    return activeServices.map((service, index) => {
      return (
        <div key={index} className="card-header sm">
          <div className="row">
            <div className="col-xs-3">
              <div className="typo-strong-150">{service.title}</div>
            </div>
            <div className="col-xs-3">
              <div className="typo-normal-150">{service.description || '-'}</div>
            </div>
            <div className="col-xs-2 text-end">
              <div className="typo-normal-150">{service.code}</div>
            </div>
            <div className="col-xs-2 text-end">
              <div className="typo-normal-150">${service.price.toFixed(2)}</div>
            </div>
            <div className="col-xs-2 text-end">
              <div className="item grey typo-normal-150 me-2" onClick={() => openEditServiceModal(service)}>Edit</div>
              <div className="item red typo-normal-150" onClick={() => deleteService(service.id)}>Delete</div>
            </div>
          </div>
        </div>
      );
    });
  }

  if (loading) {
    return <></>;
  }

  return (
    <>
      <Helmet title="Settings | Partner Edit" />

      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="d-flex justify-content-between align-items-center">
              <div className="">
                <div className="typo-hero-small">Partner Update</div>
                <div className="typo-normal-175">Update partner organization and manage its details</div>
              </div>
              <div className="w-25 text-end">
                {/* <div className="item typo-normal-150 ms-2" onClick={() => navigate('/patient/create')}><FilePlus height={16} width={16} color={'grey'} /> Create Patient</div> */}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="typo-strong-200">General information</div>
              </div>
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-3">
                    <div className="typo-strong-150">Title <span className="text-red">*</span></div>
                    <input type="text" placeholder="" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={updateTitle} className="input sm typo-normal-150 w-100" />
                  </div>
                  <div className="col-xs-12 col-lg-6">
                    <div className="typo-strong-150">Description</div>
                    <input type="text" placeholder="" value={description} onChange={(e) => setDescription(e.target.value)} onBlur={updateDescription} className="input sm typo-normal-150 w-100" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="typo-strong-200">Partner Portal</div>
              </div>
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-3">
                    <div className="typo-strong-150">Activate partner portal?</div>
                    <div className={"item grey typo-normal-150 me-2 " + (partnerPortal ? " blue" : "")} onClick={() => updatePartnerPortal(true)}>Yes</div>
                    <div className={"item grey typo-normal-150 me-2 " + (!partnerPortal ? " blue" : "")} onClick={() => updatePartnerPortal(false)}>No</div>

                    <div className="typo-normal-125 mt-1">{partnerPortal ? "Deactivation will turn off the partner portal" : "Activation will generate a new password automatically"}</div>
                  </div>
                  {partnerPortal && (
                    <div className="col-xs-12 col-lg-6">
                      <div className="typo-strong-150">Login</div>

                      <div className="d-flex justify-content-start align-items-center">
                        <input type="text" placeholder="" value={partnerPortalLogin} className="input sm typo-normal-150 w-75" readOnly={true} />
                        <div className="item typo-normal-150 ms-2" onClick={copyPartnerPortalLogin}><Copy height={16} width={16} color={'grey'} /> Copy</div>
                      </div>
                      
                      <div className="typo-strong-150 mt-2">Password</div>
                      {partnerPortalPassword ? (
                        <>
                          <div className="d-flex justify-content-start align-items-center">
                            <input type="password" placeholder="" value={partnerPortalPassword} className="input sm typo-normal-150 w-75" readOnly={true} />
                            <div className="item typo-normal-150 ms-2" onClick={copyPartnerPortalPassword}><Copy height={16} width={16} color={'grey'} /> Copy</div>
                          </div>
                          <div className="typo-normal-125 mt-1">Copy this password now. It won't be shown again.</div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-start align-items-center">
                          <input type="text" placeholder="" value="••••••••" className="input sm typo-normal-150 w-75" readOnly={true} />
                          <div className="item typo-normal-150 ms-2" onClick={resetPartnerPortalPassword}>Reset password</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="typo-strong-200">Referral Provider</div>
              </div>
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-12 col-lg-3">
                    <div className="typo-strong-150">Can submit referral?</div>
                    <div className={"item grey typo-normal-150 me-2 " + (referralProvider ? " blue" : "")} onClick={() => updateReferralProvider(true)}>Yes</div>
                    <div className={"item grey typo-normal-150 me-2 " + (!referralProvider ? " blue" : "")} onClick={() => updateReferralProvider(false)}>No</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="typo-strong-200">Service Provider</div>
              </div>
              <div className="card-header sm">
                <div className="typo-strong-150">Allowed to provide services?</div>
                <div className={"item grey typo-normal-150 me-2 " + (serviceProvider ? " blue" : "")} onClick={() => updateServiceProvider(true)}>Yes</div>
                <div className={"item grey typo-normal-150 me-2 " + (!serviceProvider ? " blue" : "")} onClick={() => updateServiceProvider(false)}>No</div>
              </div>
            </div>

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="w-75">
                    <div className="typo-strong-175">Paid Services</div>
                    <div className="typo-normal-150">This is a list of all services offered by the partner.</div>
                  </div>
                  <div className="item grey typo-normal-150 ms-2" onClick={openCreateServiceModal}><UserPlus height={16} width={16} color={'grey'} /> Create</div>
                </div>

                <div className="row">
                  <div className="col-xs-3">
                    <div className="typo-strong-150">Title</div>
                  </div>
                  <div className="col-xs-3">
                    <div className="typo-strong-150">Description</div>
                  </div>
                  <div className="col-xs-2 text-end">
                    <div className="typo-strong-150">Code</div>
                  </div>
                  <div className="col-xs-2 text-end">
                    <div className="typo-strong-150">Price</div>
                  </div>
                  <div className="col-xs-2 text-end">
                    <div className="typo-strong-150">Actions</div>
                  </div>
                </div>
              </div>
              {renderServices()}
            </div>

          </div>
        </div>
      </div>

      {serviceModal.visible && (
        <div className="fixed-bottom modal-bg" onClick={closeServiceModal}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-8 col-lg-offset-2">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">{serviceModal.mode === 'create' ? 'Create Service' : 'Edit Service'}</span><br/>
                    <span className="typo-normal-175">Enter the service details below</span>
                  </div>
                  <div className="card-content sm">
                    <div className="row">
                      <div className="col-xs-12 col-lg-6">
                        <div className="typo-strong-150">Title <span className="text-red">*</span></div>
                        <input
                          type="text"
                          placeholder="Service title"
                          value={serviceModal.title}
                          onChange={(e) => setServiceModal({...serviceModal, title: e.target.value})}
                          className="input sm typo-normal-150 w-100"
                        />
                      </div>
                      <div className="col-xs-12 col-lg-6">
                        <div className="typo-strong-150">Code</div>
                        <input
                          type="text"
                          placeholder="Service code"
                          value={serviceModal.code}
                          onChange={(e) => setServiceModal({...serviceModal, code: e.target.value})}
                          className="input sm typo-normal-150 w-100"
                        />
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-xs-12 col-lg-6">
                        <div className="typo-strong-150">Description</div>
                        <input
                          type="text"
                          placeholder="Service description"
                          value={serviceModal.description}
                          onChange={(e) => setServiceModal({...serviceModal, description: e.target.value})}
                          className="input sm typo-normal-150 w-100"
                        />
                      </div>
                      <div className="col-xs-12 col-lg-6">
                        <div className="typo-strong-150">Price <span className="text-red">*</span></div>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={serviceModal.price}
                          onChange={(e) => setServiceModal({...serviceModal, price: parseFloat(e.target.value) || 0})}
                          className="input sm typo-normal-150 w-100"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={handleServiceModalSubmit} color="blue" text={serviceModal.mode === 'create' ? 'Create' : 'Update'} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Partner;
